/**
 * Operator-facing titles for cabinet alert types, shared by the Platform health page,
 * in-app notifications and operator SMS so every surface names an issue the same way.
 *
 * Naming rule: "<part> error" when the kiosk system check reached the part but found a
 * bad state; "not responding" / "not working" only when the check is purely reachability.
 * No internal names (kiosk, tower, smart lock, POS).
 */
export const ALERT_TYPE_LABELS: Record<string, string> = {
	cabinetOffline: 'Cabinet offline',
	towerOffline: 'Cabinet offline',
	failedSystemCheck: 'Hardware check failed',
	maintenanceMode: 'Locked down',
	doorOpen: 'Door left open',
	temperature: 'Temperature out of range',
	lowStock: 'Low stock',
	emptySlots: 'Out of stock',
	noSales: 'No sales',
	salesAnomaly: 'Sales lower than usual',
	paymentTerminal: 'Payment terminal error',
	cardReader: 'Card reader error',
	smartLock: 'Fridge controller error',
	vision: 'Cameras not working',
	shelves: 'Shelf sensors not working',
	router: 'Router error',
	display: 'Top display not responding',
	etagHub: 'Price tag hub not responding',
	computer: 'Computer power issue',
	kioskError: 'Cabinet software error',
	doorUnlockFailed: 'Door failed to unlock',
	routerUptimeLow: 'Router keeps restarting',
	unstableInternet: 'Unstable internet',
}

/** `failedSystemCheck` payload keys that get bespoke labels; `posApp` is a subsystem, not an alert type. */
const SYSTEM_CHECK_SUBSYSTEM = {
	PosApp: 'posApp',
	CardReader: 'cardReader',
	SmartLock: 'smartLock',
} as const

/** Gen4 checkout app running but not in the foreground (a system-check subsystem, not an alert type of its own). */
export const POS_APP_LABEL = 'Checkout app not showing'

/** Gen4 checkout app the system check could not reach at all — reachability, so "not responding". */
export const POS_APP_UNREACHABLE_LABEL = 'Checkout app not responding'

/** A latched high temperature alarm on the fridge controller locks the cabinet for food safety. */
export const TEMPERATURE_ALARM_LABEL = 'Food safety lockout'

/** The vision QA recording test runs on the cabinet computer; a failure is a recording problem, not a camera fault. */
export const VISION_QA_LABEL = 'Camera recording test failed'

export const NON_PERISHABLE_AUTO_CLEAR_MARKER_KEY = 'non_perishable_auto_clear' as const
export const NON_PERISHABLE_AUTO_CLEAR_LABEL = 'Auto-cleared (non-perishable mode)'
export const NON_PERISHABLE_AUTO_CLEAR_PENDING_LABEL = 'Auto-clear not confirmed (non-perishable mode)'

export interface CardReaderCheck {
	message?: string
	status?: { hasPreauthWaitedTooLong?: boolean }
}

export interface SmartLockCheck {
	connected?: boolean
}

interface SystemCheckSubsystem {
	operational?: boolean
}

/** Operator wording for a card reader fault; raw terminal diagnostics are never shown as-is. */
export function cardReaderLabel(check: Record<string, unknown> | undefined): string {
	const { message = '', status } = (check ?? {}) as CardReaderCheck
	if (status?.hasPreauthWaitedTooLong || /preauth has waited too long/i.test(message)) {
		return 'Card reader stuck on a payment'
	}
	if (/timed out|ECONNREFUSED|EHOSTUNREACH|ETIMEDOUT/i.test(message)) return 'Card reader not responding'
	if (/Diagnostic: Maintenance/i.test(message)) return 'Card reader updating'
	if (/Repeated DB Message|RepeatedTransactionId/i.test(message)) return 'Card reader needs a restart'
	return ALERT_TYPE_LABELS.cardReader
}

/** Subsystems that failed a system check, from the `failedSystemCheck` alert data. */
export function failedSubsystems(data: Record<string, unknown>): string[] {
	return Object.entries(data)
		// `meta` is response-envelope data, not a subsystem (removed from new builds, still sent by older ones).
		.filter(([key, value]) => key !== 'global' && key !== 'meta' && typeof value === 'object' && value !== null)
		// Kiosk.didPassHardCheck fails shelves only when none are operational.
		.filter(([, value]) =>
			Array.isArray(value)
				? value.length > 0 && value.every((shelf) => (shelf as SystemCheckSubsystem)?.operational === false)
				: (value as SystemCheckSubsystem).operational === false
		)
		.map(([key]) => key)
}

export function smartLockLabel(check: SmartLockCheck | null | undefined): string {
	return check?.connected === false ? 'Fridge controller not responding' : ALERT_TYPE_LABELS.smartLock
}

export interface VisionSubsystemCheck {
	message?: string
	status?: {
		heartbeat?: CameraHeartbeat | string
	}
}

/** `*CameraConnected` booleans in pre-Gen4 heartbeats, mapped to display names. */
const LEGACY_CAMERA_NAMES: Record<string, string> = {
	topRightCameraConnected: 'Top right',
	topLeftCameraConnected: 'Top left',
	bottomLeftCameraConnected: 'Bottom left',
	bottomRightCameraConnected: 'Bottom right',
}

/** Gen4 `cameraStatuses` keys, mapped to display names. */
const GEN4_CAMERA_NAMES: Record<string, string> = {
	topRight: 'Top right',
	topLeft: 'Top left',
	bottomLeft: 'Bottom left',
	bottomRight: 'Bottom right',
}

export interface CameraHeartbeat {
	topRightCameraConnected?: boolean
	topLeftCameraConnected?: boolean
	bottomLeftCameraConnected?: boolean
	bottomRightCameraConnected?: boolean
	cameraStatuses?: Record<string, { connected?: boolean; disabled?: boolean; connectedOrDisabled?: boolean }>
}

/**
 * Camera positions the heartbeat reports as down. Gen4's `cameraStatuses` wins when
 * present; disabled cameras are ignored. Empty when nothing was checked or all are up.
 */
export function disconnectedCameraNames(check: unknown): string[] {
	const heartbeat = (check as VisionSubsystemCheck | undefined)?.status?.heartbeat
	if (!heartbeat || typeof heartbeat !== 'object') return []
	const statuses = heartbeat.cameraStatuses
	if (statuses && typeof statuses === 'object') {
		return Object.entries(statuses).flatMap(([field, status]) => {
			if (!status || status.disabled === true || typeof status.connected !== 'boolean') return []
			return (status.connectedOrDisabled ?? status.connected) === true ? [] : [GEN4_CAMERA_NAMES[field] ?? field]
		})
	}
	return Object.entries(LEGACY_CAMERA_NAMES).flatMap(([field, name]) =>
		heartbeat[field] === false ? [name] : []
	)
}

/**
 * True when the vision heartbeat still reports at least one enabled camera connected.
 * Used to decide whether a vision failure is a partial camera outage (warning) rather
 * than a full vision outage (blocking).
 */
export function anyCameraConnected(heartbeat: unknown): boolean {
	if (!heartbeat || typeof heartbeat !== 'object') return false
	const record = heartbeat as CameraHeartbeat
	const statuses = record.cameraStatuses
	if (statuses && typeof statuses === 'object') {
		return Object.values(statuses).some((status) =>
			!!status &&
			typeof status.connected === 'boolean' &&
			status.disabled !== true &&
			(status.connectedOrDisabled ?? status.connected) === true
		)
	}
	return Object.keys(LEGACY_CAMERA_NAMES).some((field) => record[field as keyof CameraHeartbeat] === true)
}

/**
 * A `failedSystemCheck` whose only failed subsystem is `vision` while at least one
 * camera still reports connected — the cabinet can still transact, so it surfaces as
 * a warning rather than an outage (QA-310). Vision unreachable (no heartbeat object),
 * all reported cameras down, or a second failed subsystem stay blocking.
 */
export function isPartialCameraFailure(data: unknown): boolean {
	if (!data || typeof data !== 'object' || Array.isArray(data)) return false
	const record = data as Record<string, unknown>
	const failed = failedSubsystems(record).filter((key) => key !== 'meta')
	if (failed.length !== 1 || failed[0] !== 'vision') return false
	const vision = record.vision as VisionSubsystemCheck | undefined
	return anyCameraConnected(vision?.status?.heartbeat)
}

/**
 * The kiosk appends a `QA: <error>` section to the vision check message only when the QA
 * recording test fails (e.g. recording speed too low); camera connectivity is reported
 * separately and can be all "Ok" when this fires.
 */
export function visionQaFailed(check: unknown): boolean {
	const message = (check as VisionSubsystemCheck | undefined)?.message ?? ''
	return /\bQA:/i.test(message)
}

export function visionLabel(check: unknown): string {
	if (visionQaFailed(check)) return VISION_QA_LABEL
	const down = disconnectedCameraNames(check)
	if (down.length === 1) return `${down[0]} camera not working`
	if (down.length > 1) return `${down.length} cameras not working`
	return ALERT_TYPE_LABELS.vision
}

/**
 * A `failedSystemCheck` whose only failure is the checkout app out of the
 * foreground (`isInForeground === false`): the terminal still answers the API —
 * usually sitting on the preauthorization page — so it warns instead of taking
 * the cabinet out of service (QA-312). `isInForeground === null` means the
 * probe could not reach the app at all, which stays blocking.
 */
export function isCheckoutAppBackgroundedOnly(data: Record<string, unknown> | null | undefined): boolean {
	if (!data) return false
	const failed = failedSubsystems(data)
	if (failed.length !== 1 || failed[0] !== SYSTEM_CHECK_SUBSYSTEM.PosApp) return false
	const posApp = data[SYSTEM_CHECK_SUBSYSTEM.PosApp] as
		| { status?: { isInForeground?: boolean | null } }
		| undefined
	return posApp?.status?.isInForeground === false
}

export function isCheckoutAppBackgroundedAlert(alert: { type?: unknown; data?: unknown }): boolean {
	return alert?.type === 'failedSystemCheck' && isCheckoutAppBackgroundedOnly(alert.data as Record<string, unknown> | null | undefined)
}

export function labelledFailedParts(data: Record<string, unknown>): string[] {
	return failedSubsystems(data).filter((key) => ALERT_TYPE_LABELS[key] || key === SYSTEM_CHECK_SUBSYSTEM.PosApp)
}

export function singleFailedPartLabel(data: Record<string, unknown>): string | undefined {
	const failed = labelledFailedParts(data)
	if (failed.length !== 1) return undefined
	if (failed[0] === SYSTEM_CHECK_SUBSYSTEM.PosApp) {
		return isCheckoutAppBackgroundedOnly(data) ? POS_APP_LABEL : POS_APP_UNREACHABLE_LABEL
	}
	if (failed[0] === SYSTEM_CHECK_SUBSYSTEM.CardReader) {
		return cardReaderLabel(data.cardReader as Record<string, unknown>)
	}
	if (failed[0] === SYSTEM_CHECK_SUBSYSTEM.SmartLock) {
		return smartLockLabel(data.smartLock as SmartLockCheck)
	}
	if (failed[0] === 'vision') {
		return visionLabel(data.vision as Record<string, unknown>)
	}
	return ALERT_TYPE_LABELS[failed[0]]
}

export function alertTypeLabel(type: string): string {
	if (ALERT_TYPE_LABELS[type]) return ALERT_TYPE_LABELS[type]
	// eslint-disable-next-line prefer-named-capture-group -- named groups need an ES2018 target
	const spaced = type.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ')
	return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase()
}
